<template>
  <div>
    <global-confirm-box ref="confirmBox" />

    <page-header
      :title="$t('codebook')"
      :subtitle="$t('codebook_subtitle')"
      icon="mdi-book-open-outline"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <circle-button
          :loading="loading"
          @click="load"
          icon="mdi-refresh"
          color="primary"
          :small="false"
          class="mr-2"
        />
        <primary-button @click="openAddDialog">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("codebook_add_entry") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar
      v-if="!device.isMobile"
      :search.sync="search"
      :search-placeholder="$t('search')"
      searchable
      :loading="loading"
    />

    <div class="px-6 mt-2">
      <v-data-table
        :headers="headers"
        :items="filteredItems"
        :loading="loading"
        :loading-text="$t('loading_data')"
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:item.active="{ item }">
          <v-icon :color="item.active ? 'success' : 'grey'">
            {{ item.active ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:item.price="{ item }">
          {{ item.price }} {{ restaurantCurrency }}
        </template>

        <template v-slot:item.pdv="{ item }">
          {{ item.pdv != null ? item.pdv + "%" : "-" }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              icon="mdi-pencil"
              :tooltip="$t('edit')"
              color="secondary"
              class="mr-1"
              @click="openEditDialog(item)"
            />
            <circle-button
              icon="mdi-delete"
              :tooltip="$t('delete')"
              color="error"
              @click="deleteEntry(item)"
            />
          </v-row>
        </template>
      </v-data-table>
    </div>

    <supplier-codebook-dialog
      v-model="dialog"
      :supplier-id="supplierId"
      :edited-entry="editedEntry"
      @saved="load"
    />

    <!-- Mobile FAB -->
    <div v-if="device.isMobile" class="fixed-bottom-button">
      <v-btn color="primary" block @click="openAddDialog">
        <v-icon class="mr-2">mdi-plus-circle</v-icon>
        {{ $t("codebook_add_entry") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import warehouseService from "@/services/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import SupplierCodebookDialog from "@/components/suppliers/SupplierCodebookDialog.vue";
import productCategoryService, { flattenCategories } from "@/services/productCategory";

export default {
  name: "SupplierCodebook",
  components: { SupplierCodebookDialog },
  mixins: [hasSnackbar],

  data() {
    return {
      loading: false,
      entries: [],
      search: "",
      dialog: false,
      editedEntry: null,
      categoryTree: [],
    };
  },

  computed: {
    supplierId() {
      return this.$store.state.user?.user?.suppliers?.[0]?.id;
    },
    restaurantCurrency() {
      return this.$store.state.restaurant?.settings?.price || "KM";
    },
    headers() {
      return [
        { text: this.$t("code"), value: "code", width: "120px" },
        { text: this.$t("title"), value: "name" },
        { text: this.$t("grocery"), value: "grocery_name" },
        { text: this.$t("product_categories.category"), value: "category_path" },
        {
          text: this.$t("unit_of_measurment"),
          value: "unit",
          width: "150px",
          align: "center",
        },
        { text: this.$t("price"), value: "price", align: "right", width: "120px" },
        { text: this.$t("pdv"), value: "pdv", align: "right", width: "90px" },
        { text: this.$t("active"), value: "active", align: "center", width: "120px" },
        { text: this.$t("note"), value: "notes" },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "end",
          sortable: false,
          width: "110px",
        },
      ];
    },
    categoryPaths() {
      return Object.fromEntries(flattenCategories(this.categoryTree).map((c) => [c.id, c.path]));
    },
    /** Artikli sa putanjom kategorije (stablo se može učitati poslije artikala). */
    viewEntries() {
      return this.entries.map((e) => ({ ...e, category_path: this.categoryPaths[e.category_id] || "" }));
    },
    filteredItems() {
      if (!this.search) return this.viewEntries;
      const q = this.search.toLowerCase();
      return this.viewEntries.filter(
        (e) =>
          e.code?.toLowerCase().includes(q) ||
          e.name?.toLowerCase().includes(q) ||
          e.grocery_name?.toLowerCase().includes(q)
      );
    },
  },

  mounted() {
    productCategoryService.getTree().then((tree) => (this.categoryTree = tree)).catch(() => {});
    this.load();
  },

  methods: {
    async load() {
      if (!this.supplierId) return;
      this.loading = true;
      try {
        const res = await warehouseService.getSupplierCodebook(this.supplierId);
        const raw = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        this.entries = raw.map((e) => ({
          ...e,
          grocery_name: e.grocery?.name || e.grocery_name || "",
        }));
      } catch {
        this.showErrorSnackbar(this.$t("error"));
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.editedEntry = null;
      this.dialog = true;
    },

    openEditDialog(entry) {
      this.editedEntry = { ...entry };
      this.dialog = true;
    },

    async deleteEntry(entry) {
      const confirmed = await this.$refs.confirmBox
        .open(this.$t("delete_confirmation"), this.$t("codebook_delete_confirmation"), {
          color: "error",
        })
        .catch(() => false);
      if (!confirmed) return;
      try {
        await warehouseService.deleteCodebookEntry(this.supplierId, entry.id);
        this.showSuccessSnackbar(this.$t("codebook_deleted_success"));
        this.entries = this.entries.filter((e) => e.id !== entry.id);
      } catch {
        this.showErrorSnackbar(this.$t("error"));
      }
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
</style>
